/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import React, { useState } from 'react';
import {
  Checkbox,
  Col,
  Input,
  Row,
  Select,
  Typography,
  message,
  Form
} from 'antd';
import { Button } from '@/uiKit';
import FormItem from 'antd/es/form/FormItem';
import { Link } from 'react-router-dom';
import useMergeState from '@/utils/reactUtils';
import UnauthorizedPageLayout from '@/components/unauthorizedPageLayout/UnauthorizedPageLayout';
import AppUtil from '@/utils/AppUtil';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { isEmpty, trim } from 'lodash';
import {
  delayExponentialBackoff,
  retryPromiseWithDelay
} from '@/utils/promise';
import { useAuth } from '@/provider/auth';
import ComponentFactory from '@/utils/ComponentFactory';
import AppConstants from '@/utils/AppConstants';
import classNames from 'classnames';
import styles from './Signup.module.less';
import { VerificationLink } from '../Login/Login';
import { shouldRetryQueryOnCommonErrors } from '../../utils/query';

interface SignupFormState {
  fName: string;
  lName: string;
  email: string;
  company: string;
  role?: string;
  checked: boolean;
}

export enum LoadingStateType {
  Loading = 'loading',
  Verify = 'verify',
  None = ''
}

function Signup() {
  useDocumentTitle(AppUtil.preparePageTitle('Sign up'));
  const auth = useAuth();
  const [formState, setFormState] = useMergeState<SignupFormState>({
    fName: '',
    lName: '',
    email: '',
    company: '',
    role: '',
    checked: false
  });
  const [state, setState] = useState(LoadingStateType.None);
  const [isFormEmpty, setFormEmpty] = useState(true);
  const [statusMessage, setStatusMessage] = useMergeState({
    email: ''
  });

  const checkIsEmpty = (newFormState: SignupFormState) => {
    setFormEmpty(
      isEmpty(newFormState.fName) ||
        isEmpty(newFormState.lName) ||
        isEmpty(newFormState.email) ||
        isEmpty(newFormState.company) ||
        !newFormState.checked
    );
  };

  const onLogin = async () => {
    // Call the Auth Signin API. TODO: Figure out if we want passwords
    setState(LoadingStateType.Loading);

    retryPromiseWithDelay({
      promiseFn: () =>
        auth.signup(formState, {
          callbackPathname: '/'
        }),
      errorPredicate: shouldRetryQueryOnCommonErrors,
      retries: 8,
      delayTime: delayExponentialBackoff
    })
      .then(() => {
        setState(LoadingStateType.Verify);
      })
      .catch((err) => {
        console.log('signup Error', JSON.stringify(err));
        setState(LoadingStateType.None);
        message.error(err.message || 'Error signing up');
      });
  };

  const handleSubmit = async () => {
    // Check if all the field is not empty then proceed to submit
    if (!isFormEmpty) {
      // TODO: Submit the email to the backend

      // TODO: If the email is successfully submitted then call
      onLogin();
    }
  };

  const handleChange = (
    key: keyof SignupFormState,
    value: string | boolean
  ) => {
    let modifiedValue = value;
    if (typeof value === 'string') {
      modifiedValue = trim(value);
    }

    if (key === 'email') {
      // Check if the email is valid email
      const validEmail = AppUtil.validateEmail(modifiedValue);
      if (!validEmail) {
        setStatusMessage({ email: 'Please enter a valid email address.' });
        return;
      }
      // Check if the email is a personal email
      const personalEmail = AppUtil.isPersonalEmail(modifiedValue);
      if (personalEmail) {
        setStatusMessage({
          email: 'Please enter your business email address.'
        });
        return;
      }
    }

    setStatusMessage({
      email: ''
    });
    const newFormState = { ...formState, [key]: value };
    setFormState({ ...newFormState });
    checkIsEmpty(newFormState);
  };

  return (
    <UnauthorizedPageLayout>
      <div className={styles.formContainer}>
        {(state === '' || state === 'loading') && (
          <>
            <Typography.Title level={3} className={styles.welcomeText}>
              Welcome to Kognitos
            </Typography.Title>
            <Typography.Text className={styles.welcomeSub}>
              Create an account to get started
            </Typography.Text>
            <Form className={styles.form} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <FormItem
                    name="fName"
                    label={<span className={styles.formLabel}>First Name</span>}
                    required
                    rules={[
                      {
                        required: true,
                        message: 'Please enter your first name'
                      },
                      {
                        pattern: /^[a-zA-Z. ]*$/,
                        message: 'Please enter a valid first name'
                      }
                    ]}
                  >
                    <Input
                      type="text"
                      required
                      onChange={(e) => handleChange('fName', e.target.value)}
                      placeholder="John"
                      data-cy="first-name"
                    />
                  </FormItem>
                </Col>
                <Col span={12}>
                  <FormItem
                    name="lName"
                    label={<span className={styles.formLabel}>Last Name</span>}
                    required
                    rules={[
                      {
                        required: true,
                        message: 'Please enter your last name'
                      },
                      {
                        pattern: /^[a-zA-Z]*$/,
                        message: 'Please enter a valid last name'
                      }
                    ]}
                  >
                    <Input
                      type="text"
                      required
                      onChange={(e) => handleChange('lName', e.target.value)}
                      placeholder="Doe"
                      data-cy="last-name"
                    />
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <FormItem
                    name="email"
                    label={
                      <span className={styles.formLabel}>Business Email</span>
                    }
                    required
                    help={
                      <span className={styles.helpText}>
                        {statusMessage.email}
                      </span>
                    }
                  >
                    <Input
                      type="email"
                      required
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="jhon@example.com"
                      data-cy="email"
                    />
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <FormItem
                    name="company"
                    label={<span className={styles.formLabel}>Company</span>}
                    required
                    rules={[
                      {
                        required: true,
                        message: 'Please enter your company name'
                      },
                      {
                        pattern: /^[a-zA-Z0-9. ]*$/,
                        message: 'Please enter a valid company name'
                      }
                    ]}
                  >
                    <Input
                      type="text"
                      required
                      onChange={(e) => handleChange('company', e.target.value)}
                      placeholder="Acme Inc."
                      data-cy="company"
                    />
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <FormItem
                    label={<span className={styles.formLabel}>Role</span>}
                  >
                    <Select
                      data-cy="sign-up-role"
                      placeholder="Select your role"
                      options={[
                        {
                          value: 'Executive Leadership',
                          label: 'Executive Leadership'
                        },
                        {
                          value: 'Technology Leadership',
                          label: 'Technology Leadership'
                        },
                        {
                          value: 'Information Leadership',
                          label: 'Information Leadership'
                        },
                        { value: 'Operations', label: 'Operations' },
                        { value: 'Engineering', label: 'Engineering' },
                        { value: 'Product', label: 'Product' },
                        { value: 'Others', label: 'Others' }
                      ]}
                      onChange={(value) => handleChange('role', value)}
                    />
                  </FormItem>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <FormItem>
                    <Checkbox
                      data-cy="terms"
                      checked={formState.checked}
                      onChange={() =>
                        handleChange('checked', !formState.checked)
                      }
                    >
                      <Typography.Text
                        className={classNames(styles.text, styles.termsText)}
                      >
                        By checking the box and clicking submit, you are
                        agreeing to the Kognitos{' '}
                        <a
                          href="https://www.kognitos.com/privacy/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          privacy policy
                        </a>
                      </Typography.Text>
                    </Checkbox>
                  </FormItem>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <FormItem>
                    <Button
                      data-cy="submit"
                      type="primary"
                      htmlType="submit"
                      disabled={isFormEmpty || state === 'loading'}
                      onClick={handleSubmit}
                      className={styles.loginButton}
                      icon={
                        LoadingStateType.Loading === state
                          ? ComponentFactory.getIcon(AppConstants.ICONS.LOADING)
                          : ''
                      }
                    >
                      {state === 'loading' ? 'Signing up...' : 'Sign Up'}
                    </Button>
                  </FormItem>
                </Col>
              </Row>
            </Form>
            <Typography.Text className={styles.text}>
              Already have an account? <Link to="/login">Log In</Link>
            </Typography.Text>
          </>
        )}

        {state === LoadingStateType.Verify && (
          <VerificationLink email={formState.email} />
        )}
      </div>
    </UnauthorizedPageLayout>
  );
}

export default Signup;
